import {
  AudioPresets,
  VideoPresets,
  type RoomOptions,
  type TrackPublishDefaults,
  type TrackPublishOptions,
  type VideoCaptureOptions,
} from 'livekit-client';
import type { HiResTuningSettings } from './HiResTuningPanel';

export type LiveKitCameraProfile = 'model' | 'adaptive';

export const ADAPTIVE_CAMERA_MAX_BITRATE = 900_000;
export const ADAPTIVE_CAMERA_MAX_FRAMERATE = 15;

export type ActiveModelCameraParticipantParams = {
  participantId?: string | null;
  participantProfileId?: string | null;
  modelParticipantId?: string | null;
  modelProfileId?: string | null;
  modelParticipantIds?: string[];
};

export function isActiveModelCameraParticipant({
  participantId,
  participantProfileId,
  modelParticipantId,
  modelProfileId,
  modelParticipantIds,
}: ActiveModelCameraParticipantParams) {
  if (!participantId) return false;

  if (modelProfileId && participantProfileId && participantProfileId === modelProfileId) {
    return true;
  }

  if (modelParticipantIds?.includes(participantId)) {
    return true;
  }

  return !modelProfileId && participantId === modelParticipantId;
}

export function getParticipantCameraProfile(params: ActiveModelCameraParticipantParams): LiveKitCameraProfile {
  return isActiveModelCameraParticipant(params) ? 'model' : 'adaptive';
}

export function getLiveKitVideoResolution(settings: HiResTuningSettings) {
  let preset;

  switch (settings.liveKitResolution) {
    case '720p':
      preset = VideoPresets.h720;
      break;
    case '1080p':
      preset = VideoPresets.h1080;
      break;
    case '1440p':
      preset = VideoPresets.h1440;
      break;
    case '4k':
    default:
      preset = VideoPresets.h2160;
      break;
  }

  return {
    ...preset.resolution,
    frameRate: settings.liveKitMaxFramerate,
  };
}

export function getAdaptiveCameraCaptureOptions(deviceId?: string | null): VideoCaptureOptions {
  return {
    resolution: {
      ...VideoPresets.h540.resolution,
      frameRate: ADAPTIVE_CAMERA_MAX_FRAMERATE,
    },
    deviceId: deviceId || undefined,
    facingMode: deviceId ? undefined : 'user',
  };
}

export function getModelCameraCaptureOptions(
  tuning: HiResTuningSettings,
  deviceId?: string | null
): VideoCaptureOptions {
  return {
    resolution: getLiveKitVideoResolution(tuning),
    deviceId: deviceId || undefined,
    facingMode: deviceId ? undefined : 'user',
  };
}

export function getAdaptiveCameraPublishOptions(): TrackPublishDefaults {
  return {
    videoCodec: 'h264',
    simulcast: true,
    videoEncoding: {
      maxBitrate: ADAPTIVE_CAMERA_MAX_BITRATE,
      maxFramerate: ADAPTIVE_CAMERA_MAX_FRAMERATE,
    },
    videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
    degradationPreference: 'balanced',
  };
}

export function getModelCameraPublishOptions(tuning: HiResTuningSettings): TrackPublishDefaults {
  const publishOptions: TrackPublishDefaults = {
    videoCodec: tuning.liveKitCodec,
    simulcast: tuning.liveKitSimulcast,
    videoEncoding: {
      maxBitrate: tuning.liveKitMaxBitrateMbps * 1_000_000,
      maxFramerate: tuning.liveKitMaxFramerate,
    },
    degradationPreference: tuning.liveKitDegradationPreference,
  };

  if (tuning.liveKitSimulcast) {
    publishOptions.videoSimulcastLayers = [VideoPresets.h180, VideoPresets.h540];
  }

  return publishOptions;
}

export function getCameraCaptureOptions(
  profile: LiveKitCameraProfile,
  tuning: HiResTuningSettings,
  deviceId?: string | null
) {
  return profile === 'model'
    ? getModelCameraCaptureOptions(tuning, deviceId)
    : getAdaptiveCameraCaptureOptions(deviceId);
}

export function getCameraPublishOptions(
  profile: LiveKitCameraProfile,
  tuning: HiResTuningSettings
): TrackPublishDefaults {
  return profile === 'model'
    ? getModelCameraPublishOptions(tuning)
    : getAdaptiveCameraPublishOptions();
}

export function getCameraEnableOptions(
  profile: LiveKitCameraProfile,
  tuning: HiResTuningSettings,
  deviceId?: string | null
): {
  captureOptions: VideoCaptureOptions;
  publishOptions: TrackPublishOptions;
} {
  return {
    captureOptions: getCameraCaptureOptions(profile, tuning, deviceId),
    publishOptions: getCameraPublishOptions(profile, tuning),
  };
}

export function getLiveKitRoomOptions({
  profile,
  tuning,
  preferredCameraDeviceId,
  preferredMicrophoneDeviceId,
}: {
  profile: LiveKitCameraProfile;
  tuning: HiResTuningSettings;
  preferredCameraDeviceId?: string | null;
  preferredMicrophoneDeviceId?: string | null;
}): RoomOptions {
  return {
    adaptiveStream: {
      pixelDensity: 1,
      pauseVideoInBackground: true,
    },
    dynacast: true,
    webAudioMix: true,
    videoCaptureDefaults: getCameraCaptureOptions(profile, tuning, preferredCameraDeviceId),
    publishDefaults: {
      ...getCameraPublishOptions(profile, tuning),
      audioPreset: AudioPresets.music,
      dtx: false,
    },
    audioCaptureDefaults: {
      deviceId: preferredMicrophoneDeviceId || undefined,
      echoCancellation: true,
      noiseSuppression: false,
      voiceIsolation: false,
      autoGainControl: false,
      sampleRate: 48_000,
    },
  };
}
